import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import qrScanNoticeService from '../../services/qrScanNoticeService.js';

const ROTATE_MS = 4000;
const POLL_MS = 45000;

const kindLabel = (kind) => (kind === 'crossing' ? 'Crossing scan' : 'Status scan');

const AgentScanNoticeCard = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  const load = useCallback(async () => {
    try {
      const list = await qrScanNoticeService.list(false);
      const next = list?.data || [];
      setItems(next);
      setIndex((current) => (next.length ? current % next.length : 0));
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (items.length < 2) return undefined;
    const timer = setInterval(() => {
      setVisible(false);
      window.setTimeout(() => {
        setIndex((current) => (current + 1) % items.length);
        setVisible(true);
      }, 280);
    }, ROTATE_MS);
    return () => clearInterval(timer);
  }, [items.length]);

  const current = items[index] || null;

  const checkCurrent = async (event) => {
    event.stopPropagation();
    if (!current) return;
    await qrScanNoticeService.check(current.id);
    await load();
  };

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={() => navigate('/qr-scan-notices')}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          navigate('/qr-scan-notices');
        }
      }}
      className="bg-white rounded-xl shadow-sm border border-amber-200 p-3 sm:p-4 min-h-[76px] sm:min-h-[90px] relative overflow-hidden cursor-pointer hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
    >
      <p className="text-gray-500 text-[11px] sm:text-sm font-medium leading-snug pr-8">Scan notice</p>
      <div className={`mt-1 transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}>
        {current ? (
          <>
            <p className="text-sm sm:text-base font-bold text-gray-900 leading-tight">
              {current.shipment_id}
            </p>
            <p className="text-[11px] sm:text-xs text-amber-800 mt-0.5 line-clamp-2">
              {kindLabel(current.scan_kind)} at {current.scanned_hub_name || 'another branch'}
            </p>
            <button
              type="button"
              onClick={checkCurrent}
              className="mt-1.5 text-[11px] sm:text-xs font-semibold text-orange-600 hover:text-orange-700"
            >
              Check
            </button>
          </>
        ) : (
          <p className="text-sm text-gray-400 mt-1">No new scans</p>
        )}
      </div>
      {items.length > 1 && (
        <p className="text-[10px] text-gray-400 mt-1">{index + 1} / {items.length}</p>
      )}
      <div className="p-1.5 sm:p-2 absolute rounded-lg right-2 bottom-2 sm:right-3 sm:bottom-3 bg-orange-500">
        <Bell className="w-5 h-5 text-white" />
      </div>
    </div>
  );
};

export default AgentScanNoticeCard;
